/**
 * My Listings Page
 * Professional seller dashboard for managing marketplace listings
 * Features tabbed views, stats overview, and inline actions
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { Slider } from 'primereact/slider';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Skeleton } from 'primereact/skeleton';
import { ProgressBar } from 'primereact/progressbar';
import { SelectButton } from 'primereact/selectbutton';
import { Paginator } from 'primereact/paginator';
import { Menu } from 'primereact/menu';
import { LeadGradeBadge, ScopeBadge } from './components';
// Shared rich decision card (same component as marketplace Browse) so My
// Listings renders identically — owner actions injected via renderActions.
import SharedLeadCard from './components/ListingCard';
import {
    marketplaceService,
    PRODUCT_TYPE_DETAILED_OPTIONS,
    formatLoanAmount,
    formatTimeAgo,
    getLoanProductConfig,
    getStatusConfig,
    getDaysUntilExpiry,
    getGradeConfig
} from '../../services/marketplaceService';
import NavigationTabMenu from '../../components/common/NavigationTabMenu';

// Sell Leads section tabs
const SELL_LEADS_TABS = [
    { label: 'My Listings', icon: 'pi pi-list', route: '/app/marketplace/my-listings' },
    { label: 'Buyer Enquiries', icon: 'pi pi-inbox', route: '/app/marketplace/received-interests' },
    { label: 'Post My Lead', icon: 'pi pi-plus', route: '/app/marketplace/post' }
];

// Constants defined outside component to avoid recreating on every render
const STATUS_FILTERS = ['all', 'active', 'converted', 'closed'];
const ROWS_PER_PAGE = 12;

const LOAN_TYPE_OPTIONS = PRODUCT_TYPE_DETAILED_OPTIONS;

const SCOPE_OPTIONS = [
    { label: 'All', value: 'all' },
    { label: 'Public', value: 'public', icon: 'pi pi-globe' },
    { label: 'Network', value: 'network', icon: 'pi pi-users' }
];

// Outline style for Tags - border only, no solid fill
const outlineTagStyle = (severity) => {
    const colors = {
        warning: { background: 'transparent', border: '1.5px solid #EAB308', color: '#EAB308' },
        info: { background: 'transparent', border: '1.5px solid #3B82F6', color: '#3B82F6' },
        success: { background: 'transparent', border: '1.5px solid #22C55E', color: '#22C55E' },
        danger: { background: 'transparent', border: '1.5px solid #EF4444', color: '#EF4444' },
        secondary: { background: 'transparent', border: '1.5px solid #94A3B8', color: '#64748B' },
        help: { background: 'transparent', border: '1.5px solid #A855F7', color: '#A855F7' },
    };
    return colors[severity] || colors.secondary;
};

const MyListings = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const searchTimeoutRef = useRef(null);

    // State
    const [loading, setLoading] = useState(true);
    const [listings, setListings] = useState([]);
    const [stats, setStats] = useState({ total: 0, active: 0, converted: 0, closed: 0 });
    const [activeTab, setActiveTab] = useState(0);
    const [selectedListing, setSelectedListing] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

    // Filter states
    const [scopeFilter, setScopeFilter] = useState('all');
    const [loanTypeFilter, setLoanTypeFilter] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    // Pagination state
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);

    // View mode state - default to grid (cards) view
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const viewOptions = [
        { icon: 'pi pi-th-large', value: 'grid' },
        { icon: 'pi pi-list', value: 'list' }
    ];

    // Dialog states
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showRelistDialog, setShowRelistDialog] = useState(false);
    const [editData, setEditData] = useState({ commission: 10, maxSpots: 1, expiresAt: null });
    const [saving, setSaving] = useState(false);

    // Debounce search query and reset pagination when search changes
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            // Reset pagination when search term changes
            setFirst(0);
            setPage(1);
            setDebouncedSearchQuery(searchQuery);
        }, 500);
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    // Check if any filter is active
    const activeFiltersCount = [
        debouncedSearchQuery,
        scopeFilter !== 'all' ? scopeFilter : null,
        loanTypeFilter,
        startDate,
        endDate
    ].filter(Boolean).length;

    // Clear all filters
    const clearFilters = () => {
        setSearchQuery('');
        setDebouncedSearchQuery('');
        setScopeFilter('all');
        setLoanTypeFilter(null);
        setStartDate(null);
        setEndDate(null);
    };

    // Helper to get lead data from listing (handles lead_snapshot)
    const getLeadData = (listing) => {
        // Check if data is nested in lead_snapshot or lead, otherwise use listing itself
        return listing.lead_snapshot || listing.lead || listing;
    };

    // Helper to get borrower name
    const getBorrowerName = (listing) => {
        const lead = getLeadData(listing);
        if (lead.borrower_name) return lead.borrower_name;
        if (lead.masked_name) return lead.masked_name;
        if (lead.full_name) return lead.full_name;
        if (lead.first_name || lead.last_name) {
            return `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
        }
        return 'Unknown Borrower';
    };

    // Helper to get loan amount
    const getLoanAmount = (listing) => {
        const lead = getLeadData(listing);
        return lead.loan_amount_required || lead.loan_amount || lead.amount || 0;
    };

    // Helper to get product type
    const getProductType = (listing) => {
        const lead = getLeadData(listing);
        return lead.product_type_detailed || lead.product_type || 'personal_loan';
    };

    // Fetch listings with server-side pagination
    const fetchListings = async (status = 'all') => {
        setLoading(true);
        try {
            const params = {
                page,
                per_page: ROWS_PER_PAGE
            };
            if (status !== 'all') {
                params.status = status;
            }
            // Add search term if present
            if (debouncedSearchQuery) {
                params.search = debouncedSearchQuery;
            }
            // Add scope filter (skip if 'all')
            if (scopeFilter && scopeFilter !== 'all') {
                params.scope = scopeFilter;
            }
            // Add loan type filter (product_type_detailed matches Create Lead values)
            if (loanTypeFilter) {
                params.product_type_detailed = loanTypeFilter;
            }
            // Add date range filters
            if (startDate) {
                params.date_from = startDate.toISOString().split('T')[0];
            }
            if (endDate) {
                params.date_to = endDate.toISOString().split('T')[0];
            }
            const response = await marketplaceService.getMyListings(params);

            if (response.success) {
                let listingsData = [];
                const data = response.data;

                // Handle Laravel pagination response
                if (data) {
                    if (data.data && Array.isArray(data.data)) {
                        listingsData = data.data;
                        // Extract pagination info from Laravel paginate response
                        setTotalRecords(data.total || data.data.length);
                    } else if (Array.isArray(data)) {
                        listingsData = data;
                        setTotalRecords(data.length);
                    } else if (data.listings && Array.isArray(data.listings)) {
                        listingsData = data.listings;
                        setTotalRecords(data.total || data.listings.length);
                    }
                }

                // getMyListings now returns the masked browse shape
                // (lead_details.*, borrower_preview.name, quality.grade —
                // Phase 7 Gap G). Flatten those onto each row so the card
                // helpers (which read flat keys like city/state/
                // loan_amount_required/quality_grade) keep working and the
                // confirmed location/name/amount/grade actually display.
                listingsData = listingsData.map((l) => ({
                    ...l,
                    ...(l.lead_details || {}),
                    borrower_name: l.borrower_preview?.name ?? l.borrower_name,
                    quality_grade: l.quality?.grade ?? l.quality_grade,
                    quality_score: l.quality?.score ?? l.quality_score,
                }));

                setListings(listingsData);

                // Use stats from backend response (always includes all status counts)
                if (response.stats) {
                    setStats(response.stats);
                }
            }
        } catch (error) {
            console.error('Failed to fetch listings:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load your listings'
            });
        } finally {
            setLoading(false);
        }
    };

    // Reset pagination when filters change (not page itself)
    useEffect(() => {
        setFirst(0);
        setPage(1);
    }, [scopeFilter, loanTypeFilter, startDate, endDate]);

    // Fetch data when page, tab, search, or filters change
    useEffect(() => {
        fetchListings(STATUS_FILTERS[activeTab]);
    }, [activeTab, page, debouncedSearchQuery, scopeFilter, loanTypeFilter, startDate, endDate]);

    // Handle tab change with pagination reset
    const handleTabChange = (e) => {
        // Reset pagination when switching tabs
        setFirst(0);
        setPage(1);
        setActiveTab(e.index);
    };

    // Handle page change
    const onPageChange = (e) => {
        setFirst(e.first);
        setPage(Math.floor(e.first / ROWS_PER_PAGE) + 1);
    };

    // Handle edit listing
    const handleEdit = (listing) => {
        setSelectedListing(listing);
        setEditData({
            commission: Number(listing.collaboration_commission_percentage) || 2.5,
            maxSpots: Number(listing.max_collaborators) || 1,
            expiresAt: listing.expires_at ? new Date(listing.expires_at) : null
        });
        setShowEditDialog(true);
    };

    // Save edit
    const saveEdit = async () => {
        if (!selectedListing) return;
        setSaving(true);
        try {
            const response = await marketplaceService.updateListing(selectedListing.id, {
                commissionPercentage: editData.commission,
                maxSpots: editData.maxSpots,
                expiresAt: editData.expiresAt?.toISOString()
            });
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Updated',
                    detail: 'Listing updated successfully'
                });
                setShowEditDialog(false);
                fetchListings(STATUS_FILTERS[activeTab]);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to update listing'
            });
        } finally {
            setSaving(false);
        }
    };

    // Handle relist
    const handleRelist = (listing) => {
        setSelectedListing(listing);
        setEditData({
            commission: Number(listing.collaboration_commission_percentage) || 2.5,
            maxSpots: Number(listing.max_collaborators) || 1,
            expiresAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
        });
        setShowRelistDialog(true);
    };

    // Save relist
    const saveRelist = async () => {
        if (!selectedListing) return;
        setSaving(true);
        try {
            const response = await marketplaceService.relistLead(selectedListing.id, {
                commissionPercentage: editData.commission,
                expiresAt: editData.expiresAt?.toISOString()
            });
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Relisted',
                    detail: 'Lead has been relisted successfully'
                });
                setShowRelistDialog(false);
                setActiveTab(1);
                fetchListings('active');
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to relist'
            });
        } finally {
            setSaving(false);
        }
    };

    // Push refreshed lead content to a posted listing (blueprint §10).
    // Refreshes snapshot/match/quality/docs; marketplace state preserved.
    const handlePushUpdates = async (listing) => {
        try {
            const response = await marketplaceService.pushUpdates(listing.id);
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Listing refreshed',
                    detail: response.message || 'Latest lead data pushed to the marketplace.',
                });
                fetchListings(activeTab === 1 ? 'active' : 'all');
            } else {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Not refreshed',
                    detail: response.message || 'Could not push updates.',
                });
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to push updates',
            });
        }
    };

    // Handle cancel
    const handleCancel = (listing) => {
        confirmDialog({
            message: 'Are you sure you want to cancel this listing? This action cannot be undone.',
            header: 'Cancel Listing',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const response = await marketplaceService.cancelListing(listing.id);
                    if (response.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Cancelled',
                            detail: 'Listing has been cancelled'
                        });
                        fetchListings(STATUS_FILTERS[activeTab]);
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to cancel listing'
                    });
                }
            }
        });
    };

    // Get location helper
    const getLocation = (listing) => {
        const lead = getLeadData(listing);
        const city = lead.city || '';
        const state = lead.state || '';
        if (city && state) return `${city}, ${state}`;
        return city || state || lead.location || null;
    };

    // Get employment type helper
    const getEmploymentType = (listing) => {
        const lead = getLeadData(listing);
        return lead.employment_type || null;
    };

    // Owner actions kebab for the shared decision card (status-gated:
    // active → Edit / Push updates / Cancel; expired|cancelled|closed →
    // Relist). ≥3 actions → kebab menu per the design system (P18).
    const OwnerActionsMenu = ({ listing }) => {
        const menuRef = useRef(null);
        const isActive = listing.status === 'active';
        const canRelist = ['expired', 'cancelled', 'closed'].includes(listing.status);

        const items = [
            { label: 'View', icon: 'pi pi-eye', command: () => navigate(`/app/marketplace/listing/${listing.id}`) },
        ];
        if (isActive) {
            items.push({ label: 'Edit', icon: 'pi pi-pencil', command: () => handleEdit(listing) });
            items.push({ label: 'Push updates', icon: 'pi pi-cloud-upload', command: () => handlePushUpdates(listing) });
            items.push({ separator: true });
            items.push({ label: 'Cancel listing', icon: 'pi pi-times', command: () => handleCancel(listing) });
        }
        if (canRelist) {
            items.push({ label: 'Relist', icon: 'pi pi-refresh', command: () => handleRelist(listing) });
        }

        return (
            <>
                <Menu model={items} popup ref={menuRef} appendTo={typeof document !== 'undefined' ? document.body : undefined} />
                <Button
                    icon="pi pi-pencil"
                    rounded
                    text
                    size="small"
                    aria-label="Manage listing"
                    onClick={(e) => menuRef.current?.toggle(e)}
                />
            </>
        );
    };

    // List Row Component for List View
    const ListingRow = ({ listing }) => {
        const name = getBorrowerName(listing);
        const amount = getLoanAmount(listing);
        const productConfig = getLoanProductConfig(getProductType(listing));
        const gradeConfig = getGradeConfig(listing.quality_grade);
        const statusConfig = getStatusConfig(listing.status);
        const daysLeft = getDaysUntilExpiry(listing.expires_at);

        const currentSpots = listing.current_collaborators || 0;
        const maxSpots = listing.max_collaborators || 1;
        const viewCount = listing.view_count || listing.views_count || 0;
        const interestCount = listing.interest_count || 0;

        const isActive = listing.status === 'active';
        const canRelist = listing.status === 'expired' || listing.status === 'cancelled';

        return (
            <div
                className="border-bottom-1 surface-border px-2 py-2 cursor-pointer hover:surface-50 transition-colors transition-duration-150"
                onClick={() => navigate(`/app/marketplace/listing/${listing.id}`)}
            >
                <div className="flex align-items-center gap-2">
                    {/* Lead Name + Amount */}
                    <div className="flex-1" style={{ minWidth: '120px' }}>
                        <div className="font-medium text-900 text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {name}
                        </div>
                        <div className="text-green-600 font-bold text-sm">{formatLoanAmount(amount)}</div>
                    </div>

                    {/* Grade */}
                    <div className="flex-shrink-0 text-center" style={{ width: '32px' }}>
                        <div
                            className="inline-flex align-items-center justify-content-center border-round mx-auto"
                            style={{
                                width: '24px',
                                height: '24px',
                                backgroundColor: gradeConfig.bgColor,
                                color: gradeConfig.color,
                                fontWeight: 700,
                                fontSize: '0.7rem'
                            }}
                        >
                            {listing.quality_grade || '-'}
                        </div>
                    </div>

                    {/* Commission */}
                    <div className="flex-shrink-0 text-center" style={{ width: '55px' }}>
                        <div className="text-green-600 font-bold text-sm">{listing.collaboration_commission_percentage || 0}%</div>
                    </div>

                    {/* Spots */}
                    <div className="flex-shrink-0 text-center hidden lg:block" style={{ width: '45px' }}>
                        <div className="text-600 font-medium text-sm">{currentSpots}/{maxSpots}</div>
                    </div>

                    {/* Engagement */}
                    <div className="flex-shrink-0 text-center hidden xl:flex align-items-center gap-1" style={{ width: '60px' }}>
                        <span className="flex align-items-center text-500 text-xs">
                            <i className="pi pi-eye mr-1" style={{ fontSize: '0.65rem' }} />
                            {viewCount}
                        </span>
                        <span className="flex align-items-center text-pink-500 text-xs">
                            <i className="pi pi-heart-fill mr-1" style={{ fontSize: '0.65rem' }} />
                            {interestCount}
                        </span>
                    </div>

                    {/* Status */}
                    <div className="flex-shrink-0 text-center hidden md:block" style={{ width: '60px' }}>
                        <Tag
                            value={statusConfig.label}
                            style={{ ...outlineTagStyle(statusConfig.severity), fontSize: '0.65rem', padding: '2px 6px' }}
                            className="text-xs"
                        />
                    </div>

                    {/* Expires */}
                    <div className="flex-shrink-0 text-center hidden xl:block" style={{ width: '60px' }}>
                        {listing.status === 'active' && daysLeft > 0 ? (
                            <span className={`text-xs ${daysLeft <= 3 ? 'text-orange-500 font-semibold' : 'text-600'}`}>
                                {daysLeft}d left
                            </span>
                        ) : (
                            <span className="text-xs text-400">-</span>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex-shrink-0 flex align-items-center gap-1">
                        <Button
                            icon="pi pi-eye"
                            className="p-button-rounded p-button-text p-button-secondary p-button-sm"
                            tooltip="View"
                            tooltipOptions={{ position: 'top' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/app/marketplace/listing/${listing.id}`);
                            }}
                        />
                        {isActive && (
                            <>
                                <Button
                                    icon="pi pi-pencil"
                                    className="p-button-rounded p-button-text p-button-success p-button-sm"
                                    tooltip="Edit"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(listing);
                                    }}
                                />
                                <Button
                                    icon="pi pi-cloud-upload"
                                    className="p-button-rounded p-button-text p-button-info p-button-sm"
                                    tooltip="Push latest lead data"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handlePushUpdates(listing);
                                    }}
                                />
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                    tooltip="Cancel"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancel(listing);
                                    }}
                                />
                            </>
                        )}
                        {canRelist && (
                            <Button
                                icon="pi pi-refresh"
                                className="p-button-rounded p-button-text p-button-help p-button-sm"
                                tooltip="Relist"
                                tooltipOptions={{ position: 'top' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRelist(listing);
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // List Header for table-like view
    const ListHeader = () => (
        <div className="surface-100 border-bottom-1 surface-border px-2 py-2 hidden md:block">
            <div className="flex align-items-center gap-2 font-semibold text-500 uppercase" style={{ fontSize: '0.65rem' }}>
                <div className="flex-1" style={{ minWidth: '120px' }}>Lead</div>
                <div className="flex-shrink-0 text-center" style={{ width: '32px' }}>Grade</div>
                <div className="flex-shrink-0 text-center" style={{ width: '55px' }}>Comm.</div>
                <div className="flex-shrink-0 text-center hidden lg:block" style={{ width: '45px' }}>Spots</div>
                <div className="flex-shrink-0 text-center hidden xl:block" style={{ width: '60px' }}>Stats</div>
                <div className="flex-shrink-0 text-center hidden md:block" style={{ width: '60px' }}>Status</div>
                <div className="flex-shrink-0 text-center hidden xl:block" style={{ width: '60px' }}>Expires</div>
                <div className="flex-shrink-0" style={{ width: '90px' }}>Actions</div>
            </div>
        </div>
    );

    // Render listings based on view mode with pagination
    const renderListingsWithPagination = () => {
        return (
            <>
                {viewMode === 'grid' ? (
                    <div className="grid p-2">
                        {listings.map((listing) => (
                            <div key={listing.id} className="col-12 md:col-6 lg:col-4 xl:col-3 p-1">
                                <SharedLeadCard
                                    listing={listing}
                                    viewMode="grid"
                                    isOwner
                                    showActions
                                    renderActions={(l) => <OwnerActionsMenu listing={l} />}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="overflow-hidden">
                        <ListHeader />
                        {listings.map((listing) => (
                            <ListingRow key={listing.id} listing={listing} />
                        ))}
                    </div>
                )}
                {/* Paginator - Always rendered to prevent CLS */}
                <div style={{ visibility: totalRecords > ROWS_PER_PAGE ? 'visible' : 'hidden' }}>
                    <Paginator
                        first={first}
                        rows={ROWS_PER_PAGE}
                        totalRecords={totalRecords || 0}
                        onPageChange={onPageChange}
                        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                        className="border-top-1 surface-border"
                        style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                        pt={{
                            root: { style: { padding: '0.25rem' } },
                            firstPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                            previousPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                            nextPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                            lastPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                            pageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } }
                        }}
                    />
                </div>
            </>
        );
    };

    // Column templates
    const borrowerTemplate = (rowData) => {
        const name = getBorrowerName(rowData);
        const amount = getLoanAmount(rowData);
        const productType = getProductType(rowData);
        const config = getLoanProductConfig(productType);

        return (
            <div className="flex align-items-center gap-3">
                <div
                    className="flex align-items-center justify-content-center border-round"
                    style={{
                        width: '40px',
                        height: '40px',
                        backgroundColor: '#eff6ff'
                    }}
                >
                    <i className={`pi ${config.icon} text-primary`} />
                </div>
                <div>
                    <div className="font-semibold text-900 mb-1">{name}</div>
                    <div className="text-green-600 font-bold">{formatLoanAmount(amount)}</div>
                </div>
            </div>
        );
    };

    const productTemplate = (rowData) => {
        const productType = getProductType(rowData);
        const config = getLoanProductConfig(productType);
        return (
            <Tag
                value={config.label}
                icon={`pi ${config.icon}`}
                style={{ ...outlineTagStyle('info') }}
                className="text-xs"
            />
        );
    };

    const gradeTemplate = (rowData) => {
        const lead = getLeadData(rowData);
        return (
            <LeadGradeBadge
                grade={rowData.quality_grade || lead.quality_grade}
                score={rowData.quality_score || lead.quality_score}
                size="small"
            />
        );
    };

    const scopeTemplate = (rowData) => (
        <ScopeBadge scope={rowData.marketplace_scope} size="small" />
    );

    const commissionTemplate = (rowData) => (
        <div className="text-center">
            <div className="text-green-600 font-bold text-lg">
                {rowData.collaboration_commission_percentage}%
            </div>
            <div className="text-400 text-xs">commission</div>
        </div>
    );

    const spotsTemplate = (rowData) => {
        const current = rowData.current_collaborators || 0;
        const max = rowData.max_collaborators || 1;
        const percentage = (current / max) * 100;

        return (
            <div style={{ minWidth: '80px' }}>
                <div className="flex justify-content-between mb-1">
                    <span className="text-600 text-sm">{current}/{max}</span>
                </div>
                <ProgressBar
                    value={percentage}
                    showValue={false}
                    style={{ height: '6px' }}
                    className={percentage >= 100 ? 'bg-red-100' : ''}
                 aria-label="Progress" />
            </div>
        );
    };

    const engagementTemplate = (rowData) => {
        const views = rowData.view_count || rowData.views_count || 0;
        const interests = rowData.interest_count || 0;

        return (
            <div className="flex gap-3">
                <div className="flex align-items-center gap-1 text-600">
                    <i className="pi pi-eye text-xs" />
                    <span className="text-sm">{views}</span>
                </div>
                <div className="flex align-items-center gap-1 text-pink-500">
                    <i className="pi pi-heart-fill text-xs" />
                    <span className="text-sm">{interests}</span>
                </div>
            </div>
        );
    };

    const statusTemplate = (rowData) => {
        const config = getStatusConfig(rowData.status);
        return (
            <Tag
                value={config.label}
                icon={`pi ${config.icon}`}
                style={{ ...outlineTagStyle(config.severity) }}
                className="text-xs"
            />
        );
    };

    const dateTemplate = (rowData) => (
        <div className="text-600 text-sm">
            <i className="pi pi-clock text-xs mr-1" />
            {formatTimeAgo(rowData.posted_at || rowData.created_at)}
        </div>
    );

    const expiryTemplate = (rowData) => {
        const days = getDaysUntilExpiry(rowData.expires_at);
        if (days <= 0) {
            return (
                <Tag value="Expired" icon="pi pi-times-circle" style={{ ...outlineTagStyle('danger') }} className="text-xs" />
            );
        }
        if (days <= 3) {
            return (
                <Tag value={`${days}d left`} icon="pi pi-clock" style={{ ...outlineTagStyle('warning') }} className="text-xs" />
            );
        }
        return (
            <span className="text-600 text-sm">
                <i className="pi pi-calendar text-xs mr-1" />
                {days}d left
            </span>
        );
    };

    const actionsTemplate = (rowData) => {
        const isActive = rowData.status === 'active';
        const canRelist = rowData.status === 'expired' || rowData.status === 'cancelled';

        return (
            <div className="flex gap-1">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-text p-button-sm"
                    tooltip="View Details"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => navigate(`/app/marketplace/listing/${rowData.id}`)}
                />
                {isActive && (
                    <>
                        <Button
                            icon="pi pi-pencil"
                            className="p-button-rounded p-button-text p-button-success p-button-sm"
                            tooltip="Edit Listing"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => handleEdit(rowData)}
                        />
                        <Button
                            icon="pi pi-times"
                            className="p-button-rounded p-button-text p-button-danger p-button-sm"
                            tooltip="Cancel Listing"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => handleCancel(rowData)}
                        />
                    </>
                )}
                {canRelist && (
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-rounded p-button-text p-button-help p-button-sm"
                        tooltip="Relist Lead"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => handleRelist(rowData)}
                    />
                )}
            </div>
        );
    };

    // Empty state
    const emptyTemplate = () => (
        <div className="flex flex-column align-items-center justify-content-center py-5">
            <div className="bg-primary-50 border-circle p-4 mb-3">
                <i className="pi pi-inbox text-primary text-3xl" />
            </div>
            <h3 className="text-900 font-semibold mb-1" style={{ fontSize: '0.95rem' }}>No Listings Found</h3>
            <p className="text-600 text-center mb-3 line-height-3" style={{ maxWidth: '300px', fontSize: '0.75rem' }}>
                You haven't posted any leads to the marketplace yet. Start by posting your first lead!
            </p>
            <Button
                label="Post Your First Lead"
                icon="pi pi-plus"
                style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                onClick={() => navigate('/app/marketplace/post')}
            />
        </div>
    );

    // Loading skeleton
    const renderSkeleton = () => (
        <div className="flex flex-column gap-2 p-2">
            {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="surface-card border-round p-2 border-1 border-200">
                    <div className="flex align-items-center gap-3">
                        <Skeleton shape="circle" size="28px" />
                        <div className="flex-1">
                            <Skeleton width="150px" height="0.85rem" className="mb-1" />
                            <Skeleton width="90px" height="0.75rem" />
                        </div>
                        <Skeleton width="60px" height="1.2rem" />
                        <Skeleton width="50px" height="1.2rem" />
                        <Skeleton width="80px" height="1.2rem" />
                    </div>
                </div>
            ))}
        </div>
    );

    // Stats configuration
    const statsConfig = [
        { key: 'total', label: 'Total Listings', icon: 'pi-list', bgColor: '#eff6ff', color: '#3b82f6' },
        { key: 'active', label: 'Active', icon: 'pi-check-circle', bgColor: '#f0fdf4', color: '#22c55e' },
        { key: 'converted', label: 'Converted', icon: 'pi-check-circle', bgColor: '#faf5ff', color: '#a855f7' },
        { key: 'closed', label: 'Closed', icon: 'pi-lock', bgColor: '#f1f5f9', color: '#64748b' }
    ];

    return (
        <div className="my-listings">
            <NavigationTabMenu tabs={SELL_LEADS_TABS} />
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Compact Header */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                {/* Row 1: Back + Title + Actions */}
                <div className="flex align-items-center justify-content-between">
                    <div className="flex align-items-center gap-1">
                        <Button
                            icon="pi pi-arrow-left"
                            className="p-button-text p-button-rounded"
                            onClick={() => navigate('/app/marketplace')}
                            tooltip="Back to Marketplace"
                            tooltipOptions={{ position: 'top' }}
                            style={{ width: '1.75rem', height: '1.75rem' }}
                        />
                        <h2 className="text-900 font-bold m-0" style={{ fontSize: '1rem' }}>My Listings</h2>
                    </div>
                    <div className="flex align-items-center gap-1">
                        {/* Desktop buttons */}
                        <Button
                            label="Browse"
                            icon="pi pi-search"
                            className="p-button-outlined hidden md:inline-flex"
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                            onClick={() => navigate('/app/marketplace')}
                        />
                        <Button
                            label="Post Lead"
                            icon="pi pi-plus"
                            className="hidden md:inline-flex"
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                            onClick={() => navigate('/app/marketplace/post')}
                        />
                        {/* Mobile buttons */}
                        <Button
                            icon="pi pi-search"
                            label="Browse"
                            className="p-button-outlined md:hidden"
                            style={{ fontSize: '0.65rem', padding: '0.3rem 0.5rem' }}
                            onClick={() => navigate('/app/marketplace')}
                        />
                        <Button
                            icon="pi pi-plus"
                            label="Post"
                            className="md:hidden"
                            style={{ fontSize: '0.65rem', padding: '0.3rem 0.5rem' }}
                            onClick={() => navigate('/app/marketplace/post')}
                        />
                    </div>
                </div>
            </div>

            {/* Filters Row - Grid layout for proper alignment */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                <div className="grid align-items-end m-0">
                    {/* Search */}
                    <div className="col-6 md:col-3 lg:col-2 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>Search</label>
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search" style={{ fontSize: '0.7rem' }} />
                            <InputText
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Name, location..."
                                className="w-full p-inputtext-sm"
                                style={{ fontSize: '0.7rem', padding: '0.5rem 0.5rem 0.5rem 2rem' }}
                            />
                        </span>
                    </div>

                    {/* Scope */}
                    <div className="col-6 md:col-2 lg:col-2 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>Network</label>
                        <Dropdown
                            value={scopeFilter}
                            options={SCOPE_OPTIONS}
                            onChange={(e) => setScopeFilter(e.value)}
                            placeholder="All"
                            className="w-full p-inputtext-sm"
                            panelStyle={{ fontSize: '0.7rem' }}
                            pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                        />
                    </div>

                    {/* Loan Type */}
                    <div className="col-6 md:col-3 lg:col-2 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>Loan Type</label>
                        <Dropdown
                            value={loanTypeFilter}
                            options={LOAN_TYPE_OPTIONS}
                            onChange={(e) => setLoanTypeFilter(e.value)}
                            placeholder="All Products"
                            optionLabel="label"
                            optionValue="value"
                            showClear
                            filter
                            filterPlaceholder="Search..."
                            className="w-full p-inputtext-sm"
                            panelStyle={{ fontSize: '0.7rem' }}
                            pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem', padding: '0.4rem 0.75rem' } }, filterInput: { style: { fontSize: '0.7rem' } } }}
                        />
                    </div>

                    {/* From Date */}
                    <div className="col-6 md:col-2 lg:col-2 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>From Date</label>
                        <Calendar
                            value={startDate}
                            onChange={(e) => setStartDate(e.value)}
                            placeholder="From"
                            className="w-full p-inputtext-sm"
                            showIcon
                            inputStyle={{ fontSize: '0.7rem' }}
                            dateFormat="dd M yy"
                            showButtonBar
                            maxDate={endDate || undefined}
                            pt={{
                                input: { style: { fontSize: '0.7rem' } },
                                dropdownButton: { style: { width: '2rem', padding: '0' } },
                                icon: { style: { fontSize: '0.75rem' } }
                            }}
                        />
                    </div>

                    {/* To Date */}
                    <div className="col-6 md:col-2 lg:col-2 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>To Date</label>
                        <Calendar
                            value={endDate}
                            onChange={(e) => setEndDate(e.value)}
                            placeholder="To"
                            className="w-full p-inputtext-sm"
                            showIcon
                            inputStyle={{ fontSize: '0.7rem' }}
                            dateFormat="dd M yy"
                            showButtonBar
                            minDate={startDate || undefined}
                            pt={{
                                input: { style: { fontSize: '0.7rem' } },
                                dropdownButton: { style: { width: '2rem', padding: '0' } },
                                icon: { style: { fontSize: '0.75rem' } }
                            }}
                        />
                    </div>

                    {/* View Toggle + Refresh */}
                    <div className="col-6 md:col-12 lg:col-2 p-1 flex align-items-end justify-content-end gap-1">
                        <div className="hidden md:flex align-items-center gap-1">
                            <SelectButton
                                value={viewMode}
                                options={viewOptions}
                                onChange={(e) => e.value && setViewMode(e.value)}
                                optionLabel="icon"
                                itemTemplate={(option) => <i className={option.icon} style={{ fontSize: '0.7rem' }} />}
                                style={{ transform: 'scale(0.85)' }}
                            />
                        </div>
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-text p-button-rounded"
                            onClick={() => fetchListings(STATUS_FILTERS[activeTab])}
                            loading={loading}
                            tooltip="Refresh"
                            tooltipOptions={{ position: 'top' }}
                            style={{ width: '1.75rem', height: '1.75rem' }}
                        />
                    </div>
                </div>

                {/* Active filters & Results count */}
                <div className="flex align-items-center justify-content-between mt-1 pt-1 border-top-1 surface-border">
                    <span className="text-500" style={{ fontSize: '0.7rem' }}>
                        {loading ? 'Loading...' : `${totalRecords} listing${totalRecords !== 1 ? 's' : ''}`}
                        {activeFiltersCount > 0 && ` \u2022 ${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''}`}
                    </span>
                    {activeFiltersCount > 0 && (
                        <Button
                            label="Clear"
                            icon="pi pi-filter-slash"
                            className="p-button-text p-button-sm"
                            onClick={clearFilters}
                            style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }}
                        />
                    )}
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid mb-2">
                {statsConfig.map((stat) => (
                    <div key={stat.key} className="col-6 md:col-3 p-1">
                        <div className="surface-card border-round shadow-1 px-2 py-2">
                            <div className="flex align-items-center gap-2">
                                <div
                                    className="flex align-items-center justify-content-center border-round flex-shrink-0"
                                    style={{
                                        width: '28px',
                                        height: '28px',
                                        backgroundColor: stat.bgColor
                                    }}
                                >
                                    <i
                                        className={`pi ${stat.icon}`}
                                        style={{ color: stat.color, fontSize: '0.8rem' }}
                                    />
                                </div>
                                <div className="min-w-0">
                                    <div
                                        className="font-bold"
                                        style={{ color: stat.color, fontSize: '1rem' }}
                                    >
                                        {stats[stat.key]}
                                    </div>
                                    <div className="text-500 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.65rem' }}>{stat.label}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Performance Summary - Hidden on mobile, compact on desktop */}
            {stats.total > 0 && (
                <div className="surface-card border-round shadow-1 p-2 mb-2 hidden md:block">
                    <div className="flex align-items-center gap-2">
                        <div className="flex-1">
                            <div className="flex align-items-center gap-2">
                                <span className="text-600 font-medium" style={{ fontSize: '0.7rem' }}>Conversion:</span>
                                <ProgressBar
                                    value={stats.total > 0 ? Math.round((stats.converted / stats.total) * 100) : 0}
                                    showValue={false}
                                    style={{ height: '4px', flex: 1, maxWidth: '160px' }}
                                 aria-label="Progress" />
                                <span className="text-600" style={{ fontSize: '0.7rem' }}>
                                    {stats.total > 0 ? Math.round((stats.converted / stats.total) * 100) : 0}%
                                </span>
                            </div>
                        </div>
                        <div className="flex gap-3 border-left-1 border-200 pl-2">
                            <div className="flex align-items-center gap-1">
                                <i className="pi pi-eye text-green-600" style={{ fontSize: '0.7rem' }} />
                                <span className="text-green-600 font-bold" style={{ fontSize: '0.75rem' }}>
                                    {listings.reduce((acc, l) => acc + (l.view_count || 0), 0)}
                                </span>
                            </div>
                            <div className="flex align-items-center gap-1">
                                <i className="pi pi-heart-fill text-pink-500" style={{ fontSize: '0.7rem' }} />
                                <span className="text-pink-500 font-bold" style={{ fontSize: '0.75rem' }}>
                                    {listings.reduce((acc, l) => acc + (l.interest_count || 0), 0)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs & DataTable */}
            <div className="surface-card border-round shadow-1">
                <div>
                    <TabView
                        activeIndex={activeTab}
                        onTabChange={handleTabChange}
                        className="marketplace-tabs"
                        pt={{
                            navContainer: { style: { fontSize: '0.8rem' } },
                            nav: { style: { padding: '0' } },
                            inkbar: { style: { height: '2px' } }
                        }}
                    >
                    <TabPanel
                        header={
                            <div className="flex align-items-center gap-1">
                                <i className="pi pi-list" style={{ fontSize: '0.75rem' }} />
                                <span>All</span>
                                <Tag value={stats.total} style={{ ...outlineTagStyle('secondary'), fontSize: '0.6rem', padding: '0.1rem 0.3rem' }} />
                            </div>
                        }
                        pt={{ headerAction: { style: { padding: '0.5rem 0.75rem', fontSize: '0.8rem' } } }}
                    >
                        {loading ? renderSkeleton() : listings.length === 0 ? emptyTemplate() : renderListingsWithPagination()}
                    </TabPanel>

                    <TabPanel
                        header={
                            <div className="flex align-items-center gap-1">
                                <i className="pi pi-check-circle text-green-500" style={{ fontSize: '0.75rem' }} />
                                <span>Active</span>
                                <Tag value={stats.active} style={{ ...outlineTagStyle('success'), fontSize: '0.6rem', padding: '0.1rem 0.3rem' }} />
                            </div>
                        }
                        pt={{ headerAction: { style: { padding: '0.5rem 0.75rem', fontSize: '0.8rem' } } }}
                    >
                        {loading ? renderSkeleton() : listings.length === 0 ? emptyTemplate() : renderListingsWithPagination()}
                    </TabPanel>

                    <TabPanel
                        header={
                            <div className="flex align-items-center gap-1">
                                <i className="pi pi-shopping-cart text-purple-500" style={{ fontSize: '0.75rem' }} />
                                <span>Converted</span>
                                <Tag value={stats.converted} style={{ ...outlineTagStyle('help'), fontSize: '0.6rem', padding: '0.1rem 0.3rem' }} />
                            </div>
                        }
                        pt={{ headerAction: { style: { padding: '0.5rem 0.75rem', fontSize: '0.8rem' } } }}
                    >
                        {loading ? renderSkeleton() : listings.length === 0 ? emptyTemplate() : renderListingsWithPagination()}
                    </TabPanel>

                    <TabPanel
                        header={
                            <div className="flex align-items-center gap-1">
                                <i className="pi pi-lock text-500" style={{ fontSize: '0.75rem' }} />
                                <span>Closed</span>
                                <Tag value={stats.closed} style={{ ...outlineTagStyle('secondary'), fontSize: '0.6rem', padding: '0.1rem 0.3rem' }} />
                            </div>
                        }
                        pt={{ headerAction: { style: { padding: '0.5rem 0.75rem', fontSize: '0.8rem' } } }}
                    >
                        {loading ? renderSkeleton() : listings.length === 0 ? emptyTemplate() : renderListingsWithPagination()}
                    </TabPanel>
                    </TabView>
                </div>
            </div>

            {/* Edit Dialog */}
            <Dialog
                visible={showEditDialog}
                onHide={() => setShowEditDialog(false)}
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-pencil text-primary" />
                        <span>Edit Listing</span>
                    </div>
                }
                style={{ width: '480px' }}
                modal
                className="p-fluid"
                footer={
                    <div className="flex justify-content-end gap-2 pt-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => setShowEditDialog(false)}
                        />
                        <Button
                            label="Save Changes"
                            icon="pi pi-check"
                            onClick={saveEdit}
                            loading={saving}
                        />
                    </div>
                }
            >
                {selectedListing && (
                    <div className="surface-100 border-round p-3 mb-4">
                        <div className="flex align-items-center gap-3">
                            <i className="pi pi-user text-primary text-xl" />
                            <div>
                                <div className="font-semibold text-900">{getBorrowerName(selectedListing)}</div>
                                <div className="text-green-600 font-bold">{formatLoanAmount(getLoanAmount(selectedListing))}</div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="field mb-4">
                    <label className="block text-900 font-medium mb-3">
                        <i className="pi pi-percentage mr-2" />
                        Lead Commission
                    </label>
                    {/* Slider with value tooltip */}
                    <div className="slider-with-tooltip mb-3" style={{ position: 'relative', paddingTop: '28px' }}>
                        <div
                            className="slider-tooltip"
                            style={{
                                position: 'absolute',
                                left: `calc(${((editData.commission - 0.5) / 9.5) * 100}% - 24px)`,
                                top: '0',
                                background: editData.commission <= 2 ? '#22C55E' : editData.commission <= 5 ? '#F59E0B' : '#EF4444',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                minWidth: '48px',
                                textAlign: 'center'
                            }}
                        >
                            {parseFloat(editData.commission.toFixed(3))}%
                        </div>
                        <Slider
                            value={editData.commission}
                            onChange={(e) => setEditData({ ...editData, commission: e.value })}
                            min={0.5}
                            max={10}
                            step={0.001}
                            className="w-full split-slider-edit"
                        />
                    </div>
                    <div className="flex justify-content-between mb-3 text-xs text-500">
                        <span>0.5%</span>
                        <span>10%</span>
                    </div>
                    {/* Direct Input */}
                    <div className="flex flex-column align-items-center gap-2 mb-2">
                        <span className="text-600 text-sm">Or enter directly:</span>
                        <InputNumber
                            value={editData.commission}
                            onValueChange={(e) => setEditData({ ...editData, commission: Math.min(10, Math.max(0.5, e.value || 0.5)) })}
                            mode="decimal"
                            minFractionDigits={1}
                            maxFractionDigits={3}
                            min={0.5}
                            max={10}
                            suffix="%"
                            inputClassName="text-center font-bold"
                            inputStyle={{
                                color: editData.commission <= 2 ? '#22C55E' : editData.commission <= 5 ? '#F59E0B' : '#EF4444',
                                fontSize: '1.25rem',
                                width: '10rem'
                            }}
                            showButtons={false}
                        />
                    </div>
                    <style>{`
                        .split-slider-edit.p-slider {
                            background: #FEE2E2 !important;
                            height: 8px !important;
                            border-radius: 4px !important;
                        }
                        .split-slider-edit .p-slider-range {
                            background: linear-gradient(to right, #22C55E, #F59E0B, #EF4444) !important;
                            border-radius: 4px 0 0 4px !important;
                        }
                        .split-slider-edit .p-slider-handle {
                            width: 20px !important;
                            height: 20px !important;
                            margin-top: -6px !important;
                            background: white !important;
                            border: 2px solid #64748b !important;
                            box-shadow: 0 1px 4px rgba(0,0,0,0.2) !important;
                        }
                        .split-slider-edit .p-slider-handle:hover {
                            border-color: #3B82F6 !important;
                        }
                    `}</style>
                </div>

                <div className="field mb-4">
                    <label className="block text-900 font-medium mb-2">
                        <i className="pi pi-users mr-2" />
                        Max Collaborators
                    </label>
                    <Dropdown
                        value={editData.maxSpots}
                        onChange={(e) => setEditData({ ...editData, maxSpots: e.value })}
                        options={[1, 2, 3, 4, 5].map(n => ({
                            label: `${n} Agent${n > 1 ? 's' : ''}`,
                            value: n
                        }))}
                        className="w-full"
                    />
                    <small className="text-500">Maximum number of agents who can work on this lead</small>
                </div>

                <div className="field">
                    <label className="block text-900 font-medium mb-2">
                        <i className="pi pi-calendar mr-2" />
                        Expiry Date
                    </label>
                    <Calendar
                        value={editData.expiresAt}
                        onChange={(e) => setEditData({ ...editData, expiresAt: e.value })}
                        minDate={new Date()}
                        className="w-full"
                        showIcon
                        dateFormat="dd M yy"
                    />
                    <small className="text-500">When this listing will expire from the marketplace</small>
                </div>
            </Dialog>

            {/* Relist Dialog */}
            <Dialog
                visible={showRelistDialog}
                onHide={() => setShowRelistDialog(false)}
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-refresh text-help" />
                        <span>Relist Lead</span>
                    </div>
                }
                style={{ width: '480px' }}
                modal
                className="p-fluid"
                footer={
                    <div className="flex justify-content-end gap-2 pt-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => setShowRelistDialog(false)}
                        />
                        <Button
                            label="Relist Now"
                            icon="pi pi-refresh"
                            className="p-button-help"
                            onClick={saveRelist}
                            loading={saving}
                        />
                    </div>
                }
            >
                <div className="bg-purple-50 border-round p-3 mb-4">
                    <div className="flex align-items-center gap-2 text-purple-700">
                        <i className="pi pi-info-circle" />
                        <span className="font-medium">Relisting will make your lead active again</span>
                    </div>
                </div>

                {selectedListing && (
                    <div className="surface-100 border-round p-3 mb-4">
                        <div className="flex align-items-center gap-3">
                            <i className="pi pi-user text-primary text-xl" />
                            <div>
                                <div className="font-semibold text-900">{getBorrowerName(selectedListing)}</div>
                                <div className="text-green-600 font-bold">{formatLoanAmount(getLoanAmount(selectedListing))}</div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="field mb-4">
                    <label className="block text-900 font-medium mb-2">
                        <i className="pi pi-percentage mr-2" />
                        Lead Commission
                    </label>
                    {/* Slider with value tooltip */}
                    <div className="slider-with-tooltip mb-3" style={{ position: 'relative', paddingTop: '28px' }}>
                        <div
                            className="slider-tooltip"
                            style={{
                                position: 'absolute',
                                left: `calc(${((editData.commission - 0.5) / 9.5) * 100}% - 24px)`,
                                top: '0',
                                background: editData.commission <= 2 ? '#22C55E' : editData.commission <= 5 ? '#F59E0B' : '#EF4444',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                minWidth: '48px',
                                textAlign: 'center'
                            }}
                        >
                            {parseFloat(editData.commission.toFixed(3))}%
                        </div>
                        <Slider
                            value={editData.commission}
                            onChange={(e) => setEditData({ ...editData, commission: e.value })}
                            min={0.5}
                            max={10}
                            step={0.001}
                            className="w-full split-slider-relist"
                        />
                    </div>
                    <div className="flex justify-content-between mb-3 text-xs text-500">
                        <span>0.5%</span>
                        <span>10%</span>
                    </div>
                    {/* Direct Input */}
                    <div className="flex flex-column align-items-center gap-2 mb-2">
                        <span className="text-600 text-sm">Or enter directly:</span>
                        <InputNumber
                            value={editData.commission}
                            onValueChange={(e) => setEditData({ ...editData, commission: Math.min(10, Math.max(0.5, e.value || 0.5)) })}
                            mode="decimal"
                            minFractionDigits={1}
                            maxFractionDigits={3}
                            min={0.5}
                            max={10}
                            suffix="%"
                            inputClassName="text-center font-bold"
                            inputStyle={{
                                color: editData.commission <= 2 ? '#22C55E' : editData.commission <= 5 ? '#F59E0B' : '#EF4444',
                                fontSize: '1.25rem',
                                width: '10rem'
                            }}
                            showButtons={false}
                        />
                    </div>
                    <style>{`
                        .split-slider-relist.p-slider {
                            background: #FEE2E2 !important;
                            height: 8px !important;
                            border-radius: 4px !important;
                        }
                        .split-slider-relist .p-slider-range {
                            background: linear-gradient(to right, #22C55E, #F59E0B, #EF4444) !important;
                            border-radius: 4px 0 0 4px !important;
                        }
                        .split-slider-relist .p-slider-handle {
                            width: 20px !important;
                            height: 20px !important;
                            margin-top: -6px !important;
                            background: white !important;
                            border: 2px solid #64748b !important;
                            box-shadow: 0 1px 4px rgba(0,0,0,0.2) !important;
                        }
                        .split-slider-relist .p-slider-handle:hover {
                            border-color: #3B82F6 !important;
                        }
                    `}</style>
                </div>

                <div className="field">
                    <label className="block text-900 font-medium mb-2">
                        <i className="pi pi-calendar mr-2" />
                        New Expiry Date
                    </label>
                    <Calendar
                        value={editData.expiresAt}
                        onChange={(e) => setEditData({ ...editData, expiresAt: e.value })}
                        minDate={new Date()}
                        className="w-full"
                        showIcon
                        dateFormat="dd M yy"
                    />
                </div>
            </Dialog>

            {/* Compact Calendar filter overrides */}
            <style>{`
                .my-listings .p-calendar .p-inputtext {
                    padding: 0.5rem 0.5rem;
                    font-size: 0.7rem;
                }
                .my-listings .p-calendar .p-datepicker-trigger {
                    width: 2rem;
                    padding: 0;
                }
                .my-listings .p-calendar .p-datepicker-trigger .p-icon {
                    width: 0.75rem;
                    height: 0.75rem;
                }
                .my-listings .p-dropdown .p-dropdown-label {
                    padding: 0.5rem 0.5rem;
                    font-size: 0.7rem;
                }
                .my-listings .p-dropdown .p-dropdown-trigger {
                    width: 2rem;
                }
                @media screen and (max-width: 768px) {
                    .my-listings .p-calendar,
                    .my-listings .p-dropdown {
                        font-size: 0.65rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default MyListings;
